'use client';
import Link from 'next/link';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { getDisplayName } from '@/lib/displayName';
import { useAuthStore } from '@/store/auth.store';
import { useMessagesStore } from '@/store/messages.store';
import { pluralReviews } from './StarRating';
import BookingDrawer from './BookingDrawer';

interface Props { specialist: any; initialDate?: string; }
type Tier = 'start' | 'pro' | 'biz';

const TIER_MAP: Record<string, Tier> = { 'Старт':'start', 'Про':'pro', 'Бизнес':'biz' };
// TIER_LABEL удалён вместе с бейджем тарифа на карточке (ТЗ 2.2).
const TIER_CFG: Record<Tier, { cardBg:string; accent:string; accentRgb:string; pillBg:string; pillColor:string }> = {
  start: { cardBg:'rgba(98,200,135,0.04)',  accent:'#62c887', accentRgb:'98,200,135',  pillBg:'rgba(98,200,135,0.15)',  pillColor:'#62c887' },
  pro:   { cardBg:'rgba(94,205,208,0.04)',  accent:'#5ecdd0', accentRgb:'94,205,208',  pillBg:'rgba(94,205,208,0.15)',  pillColor:'#5ecdd0' },
  biz:   { cardBg:'rgba(237,184,74,0.05)',  accent:'#edb84a', accentRgb:'237,184,74',  pillBg:'rgba(237,184,74,0.15)',  pillColor:'#edb84a' },
};

const getAvatarColor = (name: string) => {
  const colors = ['#307479','#7c3aed','#2563eb','#e11d48','#d97706','#059669','#4f46e5','#0891b2'];
  return colors[name.charCodeAt(0) % colors.length];
};

// formatDateShort/formatDateLabel уехали в BookingDrawer вместе с выбором слота.

const formatNextSlot = (nextSlotAt: string | null, timezone?: string) => {
  if (!nextSlotAt) return null;
  const d = new Date(nextSlotAt);
  if (d < new Date()) return null;
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const todayStr    = new Date().toLocaleDateString('sv-SE', { timeZone: tz });
  const tomorrowDate = new Date(); tomorrowDate.setDate(tomorrowDate.getDate()+1);
  const tomorrowStr  = tomorrowDate.toLocaleDateString('sv-SE', { timeZone: tz });
  const slotStr      = d.toLocaleDateString('sv-SE', { timeZone: tz });
  const weekDay = d.toLocaleDateString('ru-RU', { timeZone: tz, weekday:'short' });
  const dayLabel = slotStr === todayStr ? 'Сегодня, '+weekDay
    : slotStr === tomorrowStr ? 'Завтра, '+weekDay
    : d.toLocaleDateString('ru-RU', { timeZone: tz, day:'numeric', month:'short' })+', '+weekDay;
  const timeStr = d.toLocaleTimeString('ru-RU', { timeZone: tz, hour:'2-digit', minute:'2-digit' });
  return dayLabel + ' · ' + timeStr;
};

const getMainCategory = (profileCategory: any, services: any[]): string | null => {
  if (profileCategory?.name) return profileCategory.name;
  if (!services?.length) return null;
  const freq: Record<string, { name:string; count:number }> = {};
  for (const s of services) {
    const catId = s.category?.id ?? s.categoryId;
    const catName = s.category?.name;
    if (catId && catName) {
      if (!freq[catId]) freq[catId] = { name:catName, count:0 };
      freq[catId].count++;
    }
  }
  const entries = Object.values(freq);
  if (!entries.length) return null;
  return entries.sort((a,b) => b.count - a.count)[0].name;
};

// formatPaymentMethods удалён вместе с модалкой подтверждения (ТЗ 4).

/* === ReportModal === */
function ReportModal({ specialistId, onClose }: { specialistId: string; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const reportMutation = useMutation({
    mutationFn: (data: { reason: string; comment?: string }) =>
      api.post('/reports', { targetType:'specialist_card', targetId: specialistId, ...data }),
    onSuccess: () => {
      notify.success('Жалоба принята. Мы рассмотрим её в течение 24 часов');
      onClose();
    },
    onError: (e: any) => {
      const code = e?.response?.data?.error?.code;
      if (code === 'ALREADY_REPORTED') notify.error('Вы уже подавали жалобу на эту карточку');
      else notify.error('Не удалось отправить жалобу');
    },
  });
  return (
    <div onClick={e => e.stopPropagation()}
      style={{ position:'fixed', inset:0, zIndex:500, backgroundColor:'rgba(0,0,0,0.6)',
        display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ backgroundColor:'var(--color-bg-surface)', borderRadius:14, padding:20,
        width:'100%', maxWidth:360, boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
        <h3 style={{ margin:'0 0 4px', fontSize:15, fontWeight:700, color:'var(--color-text-primary)' }}>Пожаловаться</h3>
        <div style={{ display:'flex', flexDirection:'column', gap:8, margin:'12px 0' }}>
          {([
            { value:'fake_portfolio', label:'📷 Чужие работы / фейковое портфолио' },
            { value:'price_mismatch', label:'💰 Цены не соответствуют реальности' },
            { value:'false_info',     label:'📝 Ложная информация' },
            { value:'inappropriate',  label:'🔞 Неприемлемый контент' },
            { value:'fake_profile',   label:'👻 Фейковый профиль' },
            { value:'no_show',        label:'🚫 Не явился / игнорирует' },
            { value:'illegal',        label:'🚨 Незаконные услуги' },
            { value:'other',          label:'❓ Другое' },
          ] as const).map(({ value, label }) => (
            <label key={value} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'var(--color-text-primary)' }}>
              <input type="radio" name="report-reason" value={value} checked={reason === value}
                onChange={() => setReason(value)} style={{ accentColor:'var(--color-brand)' }} />
              {label}
            </label>
          ))}
        </div>
        <textarea value={comment} onChange={e => setComment(e.target.value)}
          placeholder="Комментарий (необязательно)" rows={2} maxLength={500}
          style={{ width:'100%', resize:'none', border:'1px solid var(--color-border-input)', borderRadius:8,
            padding:8, fontSize:13, fontFamily:'inherit', backgroundColor:'var(--color-bg-subtle)',
            color:'var(--color-text-primary)', boxSizing:'border-box', marginBottom:14 }} />
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onClose}
            style={{ flex:1, padding:9, borderRadius:8, border:'1px solid var(--color-border)',
              background:'none', cursor:'pointer', fontSize:13, color:'var(--color-text-secondary)' }}>
            Отмена
          </button>
          <button onClick={() => reportMutation.mutate({ reason, comment: comment || undefined })}
            disabled={!reason || reportMutation.isPending}
            style={{ flex:1, padding:9, borderRadius:8, border:'none',
              backgroundColor: reason ? '#e74c3c' : 'var(--color-border)',
              color:'var(--color-text-primary)', cursor: reason ? 'pointer' : 'not-allowed', fontSize:13, fontWeight:600 }}>
            {reportMutation.isPending ? 'Отправка...' : 'Отправить'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══ ГЛАВНЫЙ КОМПОНЕНТ ══════════════════════════════════════════════════════ */
export default function SpecialistCard({ specialist, initialDate }: Props) {
  const { user: authUser, isAuthenticated } = useAuthStore();
  const { setOpenWithUserId } = useMessagesStore();
  const queryClient = useQueryClient();

  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [drawerSvcId, setDrawerSvcId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen]       = useState(false);
  const [reportOpen, setReportOpen]   = useState(false);
  const [optimisticFav, setOptimisticFav] = useState<boolean | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [showReviews, setShowReviews] = useState(false);

  const {
    user, city, rating, reviewsCount, services,
    experienceYears, _count, nextSlotAt, planBadge,
    username, timezone, category, portfolioItems,
  } = specialist;
  const reviewsData = specialist.reviews ?? [];

  const tier: Tier   = TIER_MAP[planBadge ?? ''] ?? 'start';
  const cfg          = TIER_CFG[tier];
  const displayName  = getDisplayName(user);
  const profileHref  = username ? '/u/'+username : '/specialists/'+specialist.id;
  const mainCategory = getMainCategory(category, services);
  const nextSlot     = formatNextSlot(nextSlotAt ?? null, timezone);
  const isSelf       = isAuthenticated() && authUser?.id === user?.id;
  const canInteract  = isAuthenticated() && !isSelf;

  const apiBase  = process.env.NEXT_PUBLIC_API_URL?.replace('/api/v1','') ?? '';
  const resolveUrl = (url: string) => url?.startsWith('http') ? url : apiBase + url;

  const headerPhotoUrl = useMemo(() => {
    // Приоритет — фото профиля (аватар); портфолио только как fallback
    if (user?.avatarUrl) return resolveUrl(user.avatarUrl);
    const firstPortfolio = (portfolioItems ?? [])[0]?.imageUrl;
    if (firstPortfolio) return resolveUrl(firstPortfolio);
    return null;
  }, [portfolioItems, user?.avatarUrl]);

  const { data: favData } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => api.get('/favorites').then(r => r.data.data),
    enabled: canInteract, staleTime: 60_000,
  });
  const isFav = new Set((favData ?? []).map((f: any) => f.specialistId ?? f.specialist?.id)).has(specialist.id);
  const favActive = optimisticFav !== null ? optimisticFav : isFav;

  const favMutation = useMutation({
    mutationFn: (add: boolean) => add
      ? api.post('/favorites', { specialist_id: specialist.id })
      : api.delete('/favorites/'+specialist.id),
    onMutate: (add) => setOptimisticFav(add),
    onSuccess: (_, add) => { queryClient.invalidateQueries({ queryKey:['favorites'] }); notify.success(add ? 'Добавлено в избранное' : 'Удалено из избранного'); },
    onError: () => { setOptimisticFav(null); notify.error('Не удалось обновить избранное'); },
    onSettled: () => setOptimisticFav(null),
  });

  const handleFav = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (!canInteract) return;
    setMenuOpen(false);
    favMutation.mutate(!favActive);
  }, [canInteract, favActive, favMutation]);

  const handleMessage = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (!canInteract) {
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }
    if (user?.id) setOpenWithUserId(user.id);
  }, [canInteract, user?.id, setOpenWithUserId]);

  const handleCopyLink = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    navigator.clipboard.writeText(window.location.origin + profileHref).then(() => notify.success('Ссылка скопирована'));
    setMenuOpen(false);
  }, [profileHref]);

  // handleShare удалён вместе с кнопкой «поделиться» (ТЗ 2.2) — она остаётся
  // на странице профи.

  const handleReport = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    setMenuOpen(false); setReportOpen(true);
  }, []);

  const handleMenuToggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    setMenuOpen(v => !v);
  }, []);

  /** Клик по услуге и по CTA открывают одну и ту же панель записи (ТЗ 4). */
  const openBooking = useCallback((e: React.MouseEvent, svcId: string | null) => {
    e.preventDefault(); e.stopPropagation();
    setDrawerSvcId(svcId);
    setDrawerOpen(true);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = () => setMenuOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuOpen]);

  // Не больше двух услуг в карточке — остальные за строкой «ещё N услуг».
  const visibleServices = (services ?? []).slice(0, 2);
  const extraServicesCount = Math.max(0, (services?.length ?? 0) - 2);

  // Ближайший слот сегодня/завтра — хорошая новость (зелёная точка),
  // всё, что дальше — нейтральная серая. Раньше точка красилась по тарифу.
  const isSlotSoon = !!nextSlot && (nextSlot.startsWith('Сегодня') || nextSlot.startsWith('Завтра'));

  const cardStyle: React.CSSProperties = {
    // Тонировки по тарифу больше нет: без легенды два цвета фона читались
    // как разные сущности (ТЗ 2.4).
    background: 'var(--color-bg-surface)',
    border: `1px solid rgba(${cfg.accentRgb},0.18)`,
    ['--sp-accent' as any]: cfg.accent,
    ['--sp-accent-rgb' as any]: cfg.accentRgb,
    borderRadius: 16,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    // Именно height, а не minHeight (ТЗ 2.1): при min-height карточки с разным
    // числом услуг снова разъедутся по высоте и низ ряда перестанет совпадать.
    // Стало возможно только после выноса записи в drawer — раньше здесь
    // раскрывался аккордеон со слотами и ломал любую фиксированную высоту.
    // Высоту не задаём числом: каждый блок внутри имеет фиксированную высоту
    // (шапка, категория 23, услуги 89, нижняя панель), поэтому карточки
    // получаются одинаковыми по построению. Подбор числа уже приводил к тому,
    // что строка «ещё N услуг» уезжала под обрез.
  };

  return (
    <>
      {reportOpen && <ReportModal specialistId={specialist.id} onClose={() => setReportOpen(false)} />}
      {showReviews && (
        <div
          onClick={e => { e.preventDefault(); e.stopPropagation(); setShowReviews(false); }}
          style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.55)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{ background:'var(--color-bg-surface)', borderRadius:16, width:'100%', maxWidth:480,
              maxHeight:'80vh', display:'flex', flexDirection:'column',
              border:`1px solid rgba(${cfg.accentRgb},0.2)`, boxShadow:'0 8px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ padding:'16px 20px 12px', borderBottom:`0.5px solid rgba(${cfg.accentRgb},0.15)`,
              display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:'var(--color-text-primary)' }}>
                  Отзывы о {displayName}
                </div>
                {specialist.rating > 0 && (
                  <div style={{ fontSize:12, color:cfg.accent, fontWeight:600, marginTop:2 }}>
                    ★ {Number(specialist.rating).toFixed(1)} · {specialist.reviewsCount} {specialist.reviewsCount === 1 ? 'отзыв' : specialist.reviewsCount < 5 ? 'отзыва' : 'отзывов'}
                  </div>
                )}
              </div>
              <button onClick={() => setShowReviews(false)}
                style={{ width:28, height:28, borderRadius:8, border:'none', background:'var(--color-bg-muted)',
                  cursor:'pointer', fontSize:16, color:'var(--color-text-secondary)',
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                ✕
              </button>
            </div>
            <div style={{ overflowY:'auto', padding:'12px 20px 16px', flex:1 }}>
              {!reviewsData && (
                <div style={{ textAlign:'center', padding:'24px 0', color:'var(--color-text-muted)', fontSize:13 }}>
                  Загрузка...
                </div>
              )}
              {reviewsData && reviewsData.length === 0 && (
                <div style={{ textAlign:'center', padding:'24px 0', color:'var(--color-text-muted)', fontSize:13 }}>
                  Отзывов пока нет
                </div>
              )}
              {reviewsData && reviewsData.map((r: any) => (
                <div key={r.id} style={{ marginBottom:12, paddingBottom:12,
                  borderBottom:`0.5px solid rgba(${cfg.accentRgb},0.1)` }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <div style={{ width:28, height:28, borderRadius:'50%',
                        background:`rgba(${cfg.accentRgb},0.15)`, display:'flex',
                        alignItems:'center', justifyContent:'center',
                        fontSize:12, fontWeight:700, color:cfg.accent, flexShrink:0 }}>
                        {r.client?.firstName?.[0]}{r.client?.lastName?.[0]}
                      </div>
                      <div style={{ fontSize:12, fontWeight:600, color:'var(--color-text-primary)' }}>
                        {r.client?.firstName} {r.client?.lastName}
                      </div>
                    </div>
                    <div style={{ fontSize:12, color:cfg.accent, fontWeight:600 }}>
                      {'★'.repeat(Math.round(Number(r.rating)))}{'☆'.repeat(5 - Math.round(Number(r.rating)))}
                    </div>
                  </div>
                  {r.comment && (
                    <div style={{ fontSize:12, color:'var(--color-text-secondary)', lineHeight:1.5 }}>
                      {r.comment}
                    </div>
                  )}
                  <div style={{ fontSize:10, color:'var(--color-text-muted)', marginTop:4 }}>
                    {new Date(r.createdAt).toLocaleDateString('ru-RU', { day:'numeric', month:'long', year:'numeric' })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <BookingDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        specialistId={specialist.id}
        specialist={specialist}
        services={services ?? []}
        initialServiceId={drawerSvcId}
        initialDate={initialDate}
      />

      {/* Карточка целиком — ссылка на профиль. Внутренние интерактивные
          элементы (услуги, иконки, CTA) гасят всплытие сами. */}
      <Link href={profileHref} className={'sp-card t-'+tier} style={cardStyle}>

        {/* ШАПКА: фото + инфо + иконки-действия */}
        <div style={{ padding:'10px 10px 6px', flexShrink:0 }}>
          <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>

            {/* Фото 88x88 (в режиме плитки скрыто — фото является фоном карточки) */}
            <div style={{ width:88, height:88, borderRadius:12, flexShrink:0, overflow:'hidden', position:'relative' }}>
              {headerPhotoUrl ? (
                <img src={headerPhotoUrl} alt={displayName}
                  style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center top', display:'block' }} />
              ) : (
                <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center',
                  background:getAvatarColor(displayName) }}>
                  <span style={{ fontSize:32, fontWeight:700, color:'#fff' }}>{displayName.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>

            {/* Инфо */}
            <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:3, paddingTop:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:5, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:700, color:'var(--color-text-primary)', lineHeight:1.2,
                  whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', minWidth:0 }}>{displayName}</div>
                {user?.isVerified && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink:0 }}>
                    <circle cx="12" cy="12" r="11" fill="#22c55e"/>
                    <polyline points="7,12 10.5,15.5 17,9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>

              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                {/* Тарифный бейдж убран (ТЗ 2.2): внутренний прайс-лист, клиенту
                    о качестве мастера не говорит ничего. */}
                {specialist.rating > 0 && (
                  <span onClick={e => { e.preventDefault(); e.stopPropagation(); setShowReviews(true); }}
                    style={{ fontSize:11, fontWeight:700, color:'var(--color-text-body)', cursor:'pointer',
                      whiteSpace:'nowrap' }}>
                    {/* Звезда — единственное место, где остаётся янтарный (ТЗ 2.4) */}
                    <span style={{ color:'#edb84a' }}>★</span>{' '}
                    {Number(specialist.rating).toFixed(1).replace('.', ',')}
                    {specialist.reviewsCount > 0 && (
                      <span style={{ fontWeight:400, color:'var(--color-text-muted)' }}>
                        {' · '}{specialist.reviewsCount} {pluralReviews(specialist.reviewsCount)}
                      </span>
                    )}
                  </span>
                )}
                {/* Иконки действий — компактно в строке бейджей, тап-зона 32px */}
                <span style={{ display:'flex', alignItems:'center', marginLeft:'auto', flexShrink:0 }}>
                  {canInteract && (
                    <button onClick={handleMessage} title="Написать сообщение"
                      style={{ width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center',
                        background:'none', border:'none', cursor:'pointer', color:'var(--color-text-secondary)' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                    </button>
                  )}
                  {/* Сердечко: на десктопе проявляется при наведении на карточку,
                      на мобиле его нет — там пункт в меню «…» (ТЗ 2.2).
                      Уже добавленное в избранное видно всегда, иначе неясно,
                      сохранён профи или нет. */}
                  <button onClick={handleFav} className="sp-card-fav" data-active={favActive ? 'true' : 'false'}
                    title={favActive ? 'Убрать из избранного' : 'В избранное'}
                    style={{ width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center',
                      background:'none', border:'none', cursor:'pointer', color: favActive ? '#e85353' : 'var(--color-text-secondary)' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24"
                      fill={favActive ? '#e85353' : 'none'}
                      stroke={favActive ? '#e85353' : 'currentColor'}
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                  </button>
                  {/* Кнопка «поделиться» убрана (ТЗ 2.2) — она нужна на странице
                      профи, а не в списке. */}
                  {/* Меню «…»: жалоба убрана из постоянно видимой части — янтарный
                      треугольник на каждой карточке читался как «с мастером проблема». */}
                  <span ref={menuRef} style={{ position:'relative', display:'flex', alignItems:'center' }}>
                    <button onClick={handleMenuToggle} title="Ещё" aria-haspopup="menu" aria-expanded={menuOpen}
                      style={{ width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center',
                        background:'none', border:'none', cursor:'pointer', color:'var(--color-text-secondary)' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="5" cy="12" r="1.75"/><circle cx="12" cy="12" r="1.75"/><circle cx="19" cy="12" r="1.75"/>
                      </svg>
                    </button>
                    {menuOpen && (
                      <div role="menu"
                        style={{ position:'absolute', top:34, right:0, zIndex:50, minWidth:150,
                          background:'var(--color-bg-surface)', border:'1px solid var(--color-border)',
                          borderRadius:10, boxShadow:'0 6px 24px rgba(0,0,0,0.25)', overflow:'hidden',
                          textShadow:'none' }}>
                        {/* Избранное в меню — только на мобиле, где нет :hover */}
                        <button onClick={handleFav} role="menuitem" className="sp-menu-fav"
                          style={{ width:'100%', textAlign:'left', padding:'9px 12px',
                            background:'none', border:'none', cursor:'pointer', fontSize:12,
                            color:'var(--color-text-body)', whiteSpace:'nowrap' }}>
                          {favActive ? 'Убрать из избранного' : 'В избранное'}
                        </button>
                        <button onClick={handleReport} role="menuitem"
                          style={{ display:'block', width:'100%', textAlign:'left', padding:'9px 12px',
                            background:'none', border:'none', cursor:'pointer', fontSize:12,
                            // не --color-text-primary: в режиме плитки он переопределён
                            // на белый, и пункт стал бы нечитаем на светлой подложке
                            color:'var(--color-text-body)', whiteSpace:'nowrap' }}>
                          Пожаловаться
                        </button>
                      </div>
                    )}
                  </span>
                </span>
              </div>

              {/* Город показываем только когда он отличается от выбранного —
                  в своём городе это шум на каждой карточке (ТЗ 2.2).
                  fromOtherCity считает бэкенд относительно фильтра города. */}
              {(() => {
                const showCity = !!city && specialist.fromOtherCity === true;
                const expStr = experienceYears > 0
                  ? experienceYears + ' ' + (experienceYears === 1 ? 'год' : experienceYears < 5 ? 'года' : 'лет') + ' опыта'
                  : '';
                // Строку держим всегда: если её убирать при пустом содержимом,
                // шапка станет разной высоты и ряд карточек разъедется.
                return (
                  <div style={{ fontSize:11, color:'var(--color-text-muted)', whiteSpace:'nowrap',
                    overflow:'hidden', textOverflow:'ellipsis', height:14 }}>
                    {showCity ? city : ''}{showCity && expStr ? ' · ' : ''}{expStr}
                  </div>
                );
              })()}
            </div>
          </div>

        </div>

        {/* BODY — фиксированная зона: категория + услуги + пикер дат/времён */}
        <div style={{ display:'flex', flexDirection:'column', flex:1, minHeight:0, overflow:'hidden' }}>

          {/* Рендерится всегда, даже пустой: условный блок делал бы шапку
              разной высоты, и ряд карточек снова разъехался бы. */}
          <div style={{ fontSize:11, color:cfg.accent, fontWeight:600, padding:'7px 14px 3px',
            flexShrink:0, height:23, boxSizing:'border-box', overflow:'hidden',
            whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
            {mainCategory ? '● ' + mainCategory : ''}
          </div>

          {/* Высота блока задана явно и не зависит от содержимого:
              2 услуги × 34px + отступы 2 × 5px + строка «ещё N услуг» 26px.
              Высота самой услуги тоже фиксирована — иначе строка с иконкой
              выше строки без иконки, и последняя услуга уходила под обрез. */}
          <div style={{ position:'relative', flexShrink:0, height:104 }}>
            <div style={{ overflow:'hidden', height:'100%', padding:'0 10px' }}
              id={'svclist-'+specialist.id}>
              {visibleServices.map((svc: any) => {
                const priceNum = Number(svc.price);
                const priceStr = isNaN(priceNum) ? String(svc.price) : priceNum.toLocaleString('ru-RU') + ' ₽';
                const hasFrom = svc.priceType === 'from' || svc.price_type === 'from';
                return (
                  // Клик по услуге открывает панель записи с ней же — раньше
                  // здесь раскрывался аккордеон со слотами (ТЗ 4).
                  <div key={svc.id} style={{ borderRadius:9, border:`0.5px solid rgba(${cfg.accentRgb},0.2)`,
                    marginBottom:5, height:34, boxSizing:'border-box', overflow:'hidden',
                    transition:'border-color 0.15s, background 0.15s', cursor:'pointer',
                    background:`rgba(${cfg.accentRgb},0.06)` }}
                    onClick={e => openBooking(e, svc.id)}>
                    <div style={{ display:'flex', alignItems:'center', gap:7, padding:'0 9px', height:'100%' }}>
                      {svc.icon && (
                        <div style={{ width:20, height:20, borderRadius:6,
                          background:`rgba(${cfg.accentRgb},0.1)`, display:'flex',
                          alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:11 }}>
                          {svc.icon}
                        </div>
                      )}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:500, color:'var(--color-text-primary)', lineHeight:1.2,
                          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                          {svc.title}
                        </div>
                        {/* Длительность убрана (ТЗ 2.2): на выбор в списке не влияет,
                            показывается при записи. */}
                      </div>
                      <div style={{ fontSize:12, fontWeight:600, color:cfg.accent, whiteSpace:'nowrap', flexShrink:0 }}>
                        {hasFrom ? 'от ' : ''}{priceStr}
                      </div>
                    </div>
                  </div>
                );
              })}
              {extraServicesCount > 0 && (
                // Не Link: вложенная <a> внутри карточки-ссылки — невалидный HTML.
                // Ведёт на тот же профиль, поэтому просто даём клику всплыть.
                <span
                  style={{ display:'flex', alignItems:'center', justifyContent:'center',
                    height:26, boxSizing:'border-box', fontSize:11, fontWeight:600,
                    color:cfg.accent, textDecoration:'none' }}>
                  + ещё {extraServicesCount} {extraServicesCount === 1 ? 'услуга' : extraServicesCount < 5 ? 'услуги' : 'услуг'} →
                </span>
              )}
              {/* Плейсхолдер: без него карточка без услуг схлопывается
                  и ломает выравнивание ряда (ТЗ 2.1). */}
              {visibleServices.length === 0 && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%',
                  fontSize:11, color:'var(--color-text-muted)', textAlign:'center' }}>
                  Услуги пока не добавлены
                </div>
              )}
            </div>
          </div>
        </div>

        {/* НИЖНЯЯ ПАНЕЛЬ CTA */}
        <div style={{ padding:'9px 12px 11px',
          borderTop:`0.5px solid rgba(${cfg.accentRgb},0.15)`, flexShrink:0, overflow:'hidden', width:'100%', boxSizing:'border-box' }}>

          <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
            <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:11,
              color:'var(--color-text-secondary)' }}>
              {nextSlot && (
                <>
                  {/* Зелёная — запись сегодня/завтра, серая — позже.
                      Раньше точка красилась по тарифу (ТЗ 2.4). */}
                  <span style={{ width:6, height:6, borderRadius:'50%',
                    background: isSlotSoon ? '#22c55e' : 'var(--color-text-muted)',
                    flexShrink:0, display:'inline-block' }} />
                  <span>Ближайшая запись: {nextSlot}</span>
                </>
              )}
              {!nextSlot && (
                <span style={{ color:'var(--color-text-muted)' }}>Нет ближайших слотов</span>
              )}
            </div>
            {/* Один CTA на всю ширину вместо «Подробнее →» (ТЗ 2.5).
                Открывает панель записи (ТЗ 4) — высота карточки не меняется. */}
            <button type="button" onClick={e => openBooking(e, null)} className="sp-card-book-btn">
              Записаться
            </button>
          </div>
        </div>

      </Link>
    </>
  );
}
